/**
 * NC-457 — helpers for the datetime-local + date hybrid input used by RunCreateDialog.
 * Kept as a separate file so the conversion logic can be tested as a pure function
 * (MUI dropdown timing makes full-dialog jsdom tests flaky).
 */

/**
 * Convert a form value (either yyyy-MM-dd or yyyy-MM-ddTHH:mm browser-local) into the
 * payload the API expects:
 *   - empty string         → ""
 *   - "yyyy-MM-dd"         → unchanged (legacy date-only)
 *   - "yyyy-MM-ddTHH:mm"   → UTC ISO 8601 via new Date(...).toISOString()
 *   - invalid datetime     → returns the raw value so the API rejects it explicitly
 */
export function toApiDateOrIso(value: string): string {
  if (!value) return '';
  if (!value.includes('T')) return value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? value : d.toISOString();
}

/**
 * Format the current instant as a datetime-local-compatible string ("yyyy-MM-ddTHH:mm")
 * in the browser's local timezone — used as the `min` attribute for datetime-local inputs.
 */
export function nowLocalIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/**
 * NC-1287 — Quick-Modus ("jetzt starten fuer N Stunden").
 *
 * All of this is pure with an injectable `now`, same as `nowLocalIso` above, so
 * the behaviour is tested here rather than through the dialog (MUI dropdown
 * timing makes full-dialog jsdom tests flaky — see the file header).
 */

/** Duration presets offered in the quick mode, in hours. */
export const QUICK_DURATION_PRESETS = [1, 2, 4, 8] as const;

/** Upper bound for a free-form quick duration: a run longer than a fortnight is
 *  a planned course, not a spontaneous session — that is what detail mode is for. */
export const QUICK_DURATION_MAX_HOURS = 336; // 14 days

/**
 * End of a quick run as a datetime-local string ("yyyy-MM-ddTHH:mm") in the
 * browser's timezone. Crossing midnight, month and year boundaries is handled by
 * Date arithmetic, not by string maths.
 */
export function endFromDuration(hours: number, now: Date = new Date()): string {
  const end = new Date(now.getTime() + hours * 60 * 60 * 1000);
  return nowLocalIso(end);
}

/**
 * Parse a free-form duration (hours) from user input.
 * Returns null for empty, non-numeric, zero, negative and out-of-range values —
 * the caller treats null as "form not submittable".
 */
export function parseDurationHours(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return null;
  if (value <= 0 || value > QUICK_DURATION_MAX_HOURS) return null;
  return value;
}

/**
 * True when a datetime-local value lands in the local night window
 * [00:00, 08:00). Soft warning only — never a hard block (AC 4).
 * Date-only values carry no time and are never flagged.
 */
export function isNightEnd(value: string): boolean {
  if (!value || !value.includes('T')) return false;
  const d = new Date(value);
  if (isNaN(d.getTime())) return false;
  const hour = d.getHours();
  return hour >= 0 && hour < 8;
}

/**
 * True when `end` lies strictly after `start`. An empty start means "start now",
 * which any future end satisfies — the future check stays with isExpireOnValid.
 * Unparsable input returns false so the form blocks instead of sending garbage.
 */
export function isEndAfterStart(start: string, end: string): boolean {
  if (!end) return false;
  const endDate = new Date(end);
  if (isNaN(endDate.getTime())) return false;
  if (!start) return true;
  const startDate = new Date(start);
  if (isNaN(startDate.getTime())) return false;
  return endDate > startDate;
}
